/**
 * SuperBrain Builder - AI App Generator
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 *
 * Users describe an app; 6 AIs collaborate to generate a full codebase.
 * Download as ZIP or view generated files.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './BuilderPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const EXAMPLE_PROMPTS = [
  'A fitness tracker SaaS with workout logs, progress charts, and Stripe billing',
  'A real estate listing site with search, map view, and contact forms',
  'A recipe sharing platform with user ratings and favorite lists',
  'A minimal portfolio site with dark mode and animated hero',
  'A pomodoro timer with task lists and daily stats',
];

const BuilderPage = () => {
  const [prompt, setPrompt] = useState('');
  const [appName, setAppName] = useState('my-app');
  const [template, setTemplate] = useState('');
  const [stack, setStack] = useState('react-fastapi');
  const [templates, setTemplates] = useState([]);
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadTemplates();
    loadHistory();
  }, [loadTemplates, loadHistory]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/builder/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(r.data.templates || []);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/builder/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(r.data.builds || []);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  const handleBuild = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setSelectedFile(null);
    setBuilding(true);
    try {
      // Kick off async build
      const start = await axios.post(
        `${API}/builder/generate`,
        { prompt, app_name: appName, template: template || null, stack },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const buildId = start.data.build_id;

      // Poll for status
      const maxWait = 180; // 3 minutes max
      let elapsed = 0;
      let finalData = null;
      while (elapsed < maxWait) {
        await new Promise((r) => setTimeout(r, 3000));
        elapsed += 3;
        const s = await axios.get(`${API}/builder/status/${buildId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (s.data.status === 'complete') {
          finalData = s.data;
          break;
        }
        if (s.data.status === 'failed') {
          setError(s.data.error || 'Build failed');
          setBuilding(false);
          return;
        }
      }
      if (!finalData) {
        setError('Build timed out. It may still finish — check your history in a minute.');
        setBuilding(false);
        return;
      }
      setResult(finalData);
      const firstFile = Object.keys(finalData.files || {})[0];
      if (firstFile) setSelectedFile(firstFile);
      loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail !== null) {
        setError(detail.message || 'Build failed');
      } else {
        setError(detail || err.message || 'Build failed');
      }
    } finally {
      setBuilding(false);
    }
  };

  const goBack = () => { window.location.href = '/dashboard'; };

  const fileList = result ? Object.keys(result.files || {}).sort() : [];

  return (
    <div className="builder-page" data-testid="builder-page">
      <header className="builder-header">
        <div>
          <button className="back-btn" onClick={goBack} data-testid="builder-back-btn">← Dashboard</button>
          <h1>🚀 SuperBrain Builder</h1>
          <p className="builder-subtitle">
            Describe your app. 6 AIs collaborate. Get production-ready code.
          </p>
        </div>
        <button className="history-toggle" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history-btn">
          {showHistory ? 'Hide' : 'Show'} History ({history.length})
        </button>
      </header>

      <div className="builder-layout">
        <aside className="builder-form-panel">
          <form onSubmit={handleBuild} className="builder-form">
            <label>App Name</label>
            <input
              value={appName}
              onChange={(e) => setAppName(e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))}
              required maxLength={50}
              data-testid="builder-app-name"
            />

            <label>What do you want to build?</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. A fitness tracker SaaS with workout logs and Stripe subscriptions"
              rows={5}
              required minLength={5} maxLength={2000}
              data-testid="builder-prompt"
            />

            <label>Template (optional)</label>
            <select value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="builder-template">
              <option value="">— None / Custom —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.id}: {t.description}</option>
              ))}
            </select>

            <label>Tech Stack</label>
            <select value={stack} onChange={(e) => setStack(e.target.value)} data-testid="builder-stack">
              <option value="react-fastapi">React + Vite + FastAPI</option>
              <option value="nextjs">Next.js (full-stack)</option>
              <option value="react-only">React (frontend-only)</option>
            </select>

            <div className="example-prompts">
              <div className="example-label">💡 Ideas:</div>
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button type="button" key={i} className="example-chip"
                        onClick={() => setPrompt(ex)} data-testid={`example-prompt-${i}`}>
                  {ex.slice(0, 45)}...
                </button>
              ))}
            </div>

            {error && <div className="builder-error" data-testid="builder-error">{error}</div>}

            <button type="submit" className="builder-submit" disabled={building || !prompt}
                    data-testid="builder-submit">
              {building ? '🧠 Generating with 6 AIs (30-60s)...' : '⚡ Build My App'}
            </button>
          </form>

          {showHistory && (
            <div className="builder-history" data-testid="builder-history">
              <h3>Recent Builds</h3>
              {history.length === 0 && <div className="empty">No builds yet.</div>}
              {history.map((b) => (
                <div key={b.build_id} className="history-item">
                  <div className="history-name">{b.app_name}</div>
                  <div className="history-meta">
                    {b.models_used?.length || 0} AI models
                  </div>
                  <div className="history-prompt">{(b.prompt || '').slice(0, 80)}...</div>
                </div>
              ))}
            </div>
          )}
        </aside>

        <main className="builder-output">
          {!result && !building && (
            <div className="builder-placeholder">
              <div className="placeholder-icon">🏗️</div>
              <h2>Ready to build?</h2>
              <p>Describe your app on the left, pick a template, and hit <strong>Build My App</strong>.</p>
              <p className="placeholder-sub">
                6 AI models will collaborate to produce a complete codebase. Download as ZIP and run locally.
              </p>
            </div>
          )}

          {building && (
            <div className="builder-loading" data-testid="builder-loading">
              <div className="spinner"></div>
              <h2>AIs are collaborating...</h2>
              <ol className="build-steps">
                <li>🧠 Drafting architecture</li>
                <li>⚡ Generating code</li>
                <li>💎 Adding features</li>
                <li>🔀 Supervisor synthesizing final codebase</li>
              </ol>
              <p className="loading-note">Usually 30-60 seconds. Hang tight.</p>
            </div>
          )}

          {result && (
            <div className="builder-result" data-testid="builder-result">
              <div className="result-header">
                <div>
                  <h2>✅ {result.app_name}</h2>
                  <div className="result-meta">
                    <span className="meta-chip">🧠 {result.models_used.length} AIs</span>
                    <span className="meta-chip">📁 {fileList.length} files</span>
                  </div>
                </div>
                <div className="result-actions">
                  <span className="meta-chip" data-testid="preview-only-notice">🔒 Preview-only · in-browser</span>
                </div>
              </div>

              <div className="instructions-box">
                <h3>About this build:</h3>
                <pre>Read-only preview generated by SuperBrain's 6-AI consensus engine. Code is intended for reference inside SuperBrain only.</pre>
              </div>

              <div className="files-view">
                <div className="file-tree" data-testid="file-tree">
                  <div className="file-tree-header">Files ({fileList.length})</div>
                  {fileList.map((name) => (
                    <button key={name}
                            className={`file-item ${selectedFile === name ? 'active' : ''}`}
                            onClick={() => setSelectedFile(name)}
                            data-testid={`file-item-${name.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                      📄 {name}
                    </button>
                  ))}
                </div>
                <div className="file-preview">
                  {selectedFile ? (
                    <>
                      <div className="file-preview-header">{selectedFile}</div>
                      <pre className="file-content">{result.files[selectedFile]}</pre>
                    </>
                  ) : (
                    <div className="file-preview-empty">Select a file to preview</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <footer className="builder-footer">
        ✝ © 2026 A-1 Affiliate Marketing LLC · SuperBrain Builder
      </footer>
    </div>
  );
};

export default BuilderPage;
